import React from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import { graphql, compose, withApollo } from 'react-apollo'

import UpdateFiscalYear from './queries/UpdateFiscalYear'
import DestroyCashflowData from './queries/DestroyCashflowData'
import FinancialReviewQuery from './queries/FinancialReviewQuery'
import CacheUtil from '../common/utils/CacheUtil'
import getMostRecentData from './helpers/getMostRecentData'
import CreateCashflowDatum from './queries/CreateCashflowDatum'
import CashflowDataUtil from './helpers/CashflowDataUtil'
import businessFormOptions from './helpers/businessFormOptions'
import guarantorFormOptions from './helpers/guarantorFormOptions'
import personalDebtCategories from './helpers/personalDebtCategories'
import UpdateTaxReturnForm from './queries/UpdateTaxReturnForm'
import GuarantorCashflowQuery from './queries/GuarantorCashflowQuery'
import BusinessCashflowQuery from './queries/BusinessCashflowQuery'

const FormSelect = styled.select`
  width: 100%;
  max-width: 61px;
  height: 25px;
`
const Text = styled.div`
  text-align: right;
  font-size: 11px;
`
class FormOptions extends React.Component {
  constructor() {
    super()
    this.state = {
      selected: ''
    }
  }
  componentDidMount() {
    this.setState({ selected: this.getDefaultOption(this.props) })
  }
  componentWillReceiveProps(nextProps) {
    const { entity, resourceType } = this.props
    if (resourceType !== nextProps.resourceType || entity.rid !== nextProps.entity.rid) {
      this.setState({ selected: this.getDefaultOption(nextProps) })
    }
  }
  getOptions = resourceType => {
    if (resourceType === 'Business') {
      return businessFormOptions
    } else if (resourceType === 'Guarantor') {
      return guarantorFormOptions
    }
  }
  getDefaultOption = props => {
    const { fiscalYear, entity, data, resourceType } = props
    const { period, year } = fiscalYear
    const { taxReturns } = entity
    const taxReturn = taxReturns.find(tr => parseInt(tr.year, 10) === year)
    let formName
    if (period === 'interim') {
      return resourceType === 'Business' ? 'Interim' : 'Average'
    }
    if (taxReturn && period !== 'interim') {
      formName = taxReturn.formName
    } else if (period === 'interim') {
      formName = null
    } else {
      formName = resourceType === 'Business' ? '1120S' : '1040C'
    }
    const source = CashflowDataUtil.getSource(data, year) === 'extracted_document' ? 'manual_document' : CashflowDataUtil.getSource(data, year) || 'manual_document'
    const options = this.getOptions(resourceType)
    const selectedOption = Object.keys(options).find(form => {
      return options[form].period === period &&
             options[form].form === formName &&
             options[form].source === source
    })
    return selectedOption
  }
  renderOptions = () => {
    const { resourceType } = this.props
    return Object.keys(this.getOptions(resourceType)).map(opt => <option value={opt} key={opt}>{opt}</option>)
  }
  onSourceChange = async e => {
    const { resourceType, fiscalYear, onFormOptionsChange } = this.props
    const { year } = fiscalYear
    const { value } = e.target
    const options = this.getOptions(resourceType)
    await this.setState({
      selected: value
    })
    if (options[value].period !== 'interim') {
      await this._changeForm()
    }
    await this._changeSource()
    await this._changePeriod()

    const { selected } = this.state
    const { form } = options[selected]
    onFormOptionsChange(form || 'interim', year)
  }
  _changeForm = async () => {
    const { updateTaxReturnForm, entity, resourceType, fiscalYear, queriedYears, cashflowOptions, loan } = this.props
    const { selected } = this.state
    const { taxReturns } = entity
    const { year } = fiscalYear
    const options = this.getOptions(resourceType)
    const { form } = options[selected]
    const taxReturn = taxReturns.find(tr => parseInt(tr.year, 10) === year)
    const queryVariables = {
      years: queriedYears,
      cashflowOptions,
      loanId: loan.rid
    }
    if (taxReturn) {
      const { rid, formName } = taxReturn
      if (formName !== form) {
        updateTaxReturnForm({
          variables: {
            input: {
              rid,
              formName: form
            }
          },
          optimisticResponse: {
            __typename: 'Mutation',
            updateTaxReturnForm: {
              taxReturn: {
                ...taxReturn,
                formName: form,
                __typename: 'TaxReturn'
              },
              __typename: 'UpdateTaxReturnFormPayload'
            }
          },
          update: (store, { data: { updateTaxReturnForm: { taxReturn } } }) => {
            const data = store.readQuery({ query: FinancialReviewQuery, variables: queryVariables })
            const cachedEntity = CacheUtil.findEntityInQuery(entity, data)
            const idx = cachedEntity.taxReturns.findIndex(tr => tr.year === taxReturn.year)
            cachedEntity.taxReturns[idx] = taxReturn
            store.writeQuery({ query: FinancialReviewQuery, variables: queryVariables, data })
          }
        })
      }
    }
  }
  _changePeriod = async () => {
    const {
      updateFiscalYear,
      fiscalYear,
      resourceType,
      entity,
      loan,
      cashflowOptions,
      queriedYears,
      client
    } = this.props
    const { selected } = this.state
    const { rid, year } = fiscalYear
    const options = this.getOptions(resourceType)
    const { period } = options[selected]
    const queryVariables = { years: queriedYears, loanId: loan.rid, cashflowOptions }
    if (period !== fiscalYear.period) {
      return updateFiscalYear({
        variables: {
          entityType: resourceType,
          period,
          rid
        },
        update: async (store, { data: { updateFiscalYear: { fiscalYear } } }) => {
          const data = store.readQuery({ query: FinancialReviewQuery, variables: queryVariables })
          const cachedEntity = CacheUtil.findEntityInQuery(entity, data)
          CacheUtil.replaceFiscalYear(cachedEntity, fiscalYear)
          if (!fiscalYear.optimistic) {
            if (resourceType === 'Guarantor') {
              CacheUtil.removeCashflowDataByYear(cachedEntity, fiscalYear.year, personalDebtCategories)
            }
            if (resourceType === 'Guarantor' && period === 'interim') {
              const query = await client.query({ query: GuarantorCashflowQuery, variables: { rid: entity.rid, cashflowOptions, years: queriedYears } })
              cachedEntity.cashflowData = cachedEntity.cashflowData.concat(query.data.guarantor.cashflowData)
            }
            if (resourceType === 'Business') {
              const query = await client.query({ query: BusinessCashflowQuery, variables: { rid: entity.rid, cashflowOptions, years: [year] } })
              cachedEntity.cashflowData = cachedEntity.cashflowData.concat(query.data.business.cashflowData)
            }
          }
          store.writeQuery({ query: FinancialReviewQuery, variables: queryVariables, data })
        }
      })
    }
  }
  _changeSource = async () => {
    const { data, fiscalYear, changeYearSource, resourceType, entity } = this.props
    const { selected } = this.state
    const options = this.getOptions(resourceType)
    const { period, year } = fiscalYear
    const option = options[selected]
    const currentSource = CashflowDataUtil.getSource(data, year)
    // If the period or source changes, we destroy all existing cashflow data
    const destroyCurrentData = currentSource === 'manual_transcript' && option.source === 'manual_document' || period !== option.period
    const newData = getMostRecentData(year, data).map(d => {
      const { category, year, data } = d
      return {
        category,
        year,
        data,
        period: option.period,
        source: option.source
      }
    })
    changeYearSource(year, option.source, option.period)
    if (option.source !== currentSource && period === option.period) {
      if (destroyCurrentData) {
        await this.destroyCurrentCashflowData()
      }
      await Promise.all(this.createCashflowData(newData))
      await this.updateCashflowData()
    } else if (period !== option.period) {
      if (destroyCurrentData) {
        this.destroyCurrentCashflowData()
      }
      this.removeCashflowDataByYear(entity, year)
    }
  }
  removeCashflowDataByYear = (entity, year) => {
    const { cashflowOptions, queriedYears, loan, client } = this.props
    const queryVariables = {
      query: FinancialReviewQuery,
      variables: {
        cashflowOptions,
        years: queriedYears,
        loanId: loan.rid
      }
    }
    const data = client.readQuery({ ...queryVariables })
    const cachedEntity = CacheUtil.findEntityInQuery(entity, data)
    CacheUtil.removeCashflowDataByYear(cachedEntity, year, personalDebtCategories)
    client.writeQuery({ ...queryVariables, data })
  }
  createCashflowData = data => {
    const { entity, createCashflowDatum, resourceType } = this.props
    return data.map(d => {
      return createCashflowDatum({
        variables: {
          input: {
            cashflowDatumFields: {
              resourceId: entity.rid,
              resourceType,
              year: d.year,
              category: d.category,
              data: d.data,
              source: d.source
            }
          }
        }
      })
    })
  }
  updateCashflowData = async () => {
    const { client, cashflowOptions, queriedYears, loan, fiscalYear, entity } = this.props
    const { year } = fiscalYear
    const queryVariables = { loanId: loan.rid, years: queriedYears, cashflowOptions }
    const query = await client.query({
      query:       FinancialReviewQuery,
      variables:   { ...queryVariables, years: [year] },
      fetchPolicy: 'no-cache'
    })
    const data = client.readQuery({ query: FinancialReviewQuery, variables: queryVariables })
    const queriedEntity = CacheUtil.findEntityInQuery(entity, query.data)
    const cachedEntity = CacheUtil.findEntityInQuery(entity, data)
    CacheUtil.removeCashflowDataByYear(cachedEntity, year, personalDebtCategories)
    cachedEntity.cashflowData = cachedEntity.cashflowData.concat(queriedEntity.cashflowData)
    client.writeQuery({ query: FinancialReviewQuery, variables: queryVariables, data })
  }
  destroyCurrentCashflowData = async () => {
    const { destroyCashflowData, entity, resourceType, data, fiscalYear } = this.props
    const { year } = fiscalYear
    const dataKeys = getMostRecentData(year, data).map(d => {
      const { year, category, period, source } = d
      return { year, category, period, source }
    }).filter(d => !personalDebtCategories.includes(d.category))
    if (dataKeys.length > 0) {
      return destroyCashflowData({
        variables: {
          resourceType,
          resourceId: entity.rid,
          dataKeys
        }
      })
    }
  }
  render() {
    const { isEditable, resourceType, fiscalYear, entity, data } = this.props
    const { selected } = this.state
    const { period, year } = fiscalYear
    const { taxReturns } = entity
    const hasTranscripts = data.filter(d => d.year === year).some(d => d.source === 'tax_transcript')
    const taxReturn = taxReturns.find(tr => parseInt(tr.year, 10) === year)
    const option = this.getOptions(resourceType)[selected]
    if (isEditable && !hasTranscripts) {
      return (
        <FormSelect id={`${year}_form_select`} value={selected} onChange={this.onSourceChange}>
          {this.renderOptions()}
        </FormSelect>
      )
    } else if (option && period !== 'interim') {
      return <Text>{option.form}</Text>
    } else if (taxReturn && period !== 'interim') {
      return <Text>{taxReturn.formName}</Text>
    } else {
      return null
    }
  }
}

FormOptions.propTypes = {
  createCashflowDatum: PropTypes.func,
  data: PropTypes.arrayOf(PropTypes.object),
  entity: PropTypes.shape({
    taxReturns: PropTypes.arrayOf(PropTypes.shape({ form: PropTypes.string })),
    rid: PropTypes.number.isRequired
  }),
  fiscalYear: PropTypes.shape({
    period: PropTypes.string.isRequired,
    year: PropTypes.number.isRequired
  }),
  isEditable: PropTypes.bool,
  resourceType: PropTypes.string.isRequired
}

export default compose(
  graphql(UpdateFiscalYear, {
    name: 'updateFiscalYear'
  }),
  graphql(DestroyCashflowData, {
    name: 'destroyCashflowData'
  }),
  graphql(CreateCashflowDatum, {
    name: 'createCashflowDatum'
  }),
  graphql(UpdateTaxReturnForm, {
    name: 'updateTaxReturnForm'
  }),
  withApollo
)(FormOptions)
